import type { Address } from "@/lib/types";

/**
 * Saying what is missing, before the person presses the button.
 *
 * A disabled button with no explanation is the worst thing a form can do: it
 * says no and refuses to say why, and the field at fault is usually scrolled
 * off the screen. So the same list of missing fields drives three things — the
 * red marks on the fields, the plain sentence of what is still outstanding, and
 * which input to put the cursor in when somebody presses anyway.
 *
 * The labels read as the middle of a sentence ("a phone number for collection")
 * because that is where they end up.
 */

export interface Missing {
  /** The error-map key AddressForm reads: "collection.phone". */
  key: string;
  /** The id of the input itself, for focus. Not the same shape as the key. */
  inputId: string;
  /** Lower case, no article: "phone number". */
  label: string;
  /** Which leg this belongs to, so a sentence can say it once. */
  group?: "collection" | "delivery";
}

/**
 * Required address fields, in the order they appear on screen — so the first
 * one reported is the first one a person would reach, not the first one that
 * happens to sort alphabetically.
 *
 * `id` is the input's own suffix, which is not always the field name:
 * postal_code is rendered as `collection-postal`.
 */
const ADDRESS_REQUIRED: Array<{ field: keyof Address; id: string; label: string }> = [
  { field: "contact_name", id: "contact", label: "contact name" },
  { field: "phone", id: "phone", label: "phone number" },
  { field: "street", id: "street", label: "street address" },
  { field: "suburb", id: "suburb", label: "suburb" },
  { field: "city", id: "city", label: "city" },
  { field: "postal_code", id: "postal", label: "postal code" },
  { field: "province", id: "province", label: "province" },
];

/**
 * The fields a carrier needs to price and route a leg. Not who receives it.
 *
 * This is the set a quote asks for, which mirrors the API exactly: pricing a
 * route needs a place, so a signed-in customer can pull a rate by typing one
 * delivery address and nothing else. See validateRoutable in
 * services/api/internal/api/service.go.
 */
const ROUTING_FIELDS = new Set<keyof Address>(["street", "suburb", "city", "postal_code", "province"]);

export type Purpose = "quote" | "booking";

/**
 * What is still missing from an address for a given purpose.
 *
 * "quote" asks only whether the route can be priced. "booking" adds the person
 * a driver deals with at that end, because a driver at a door with nobody to
 * call is how a delivery fails.
 */
export function missingAddressFields(
  address: Address,
  prefix: "collection" | "delivery",
  purpose: Purpose = "booking",
): Missing[] {
  return ADDRESS_REQUIRED.filter(({ field }) => {
    if (purpose === "quote" && !ROUTING_FIELDS.has(field)) return false;
    return !String(address[field] ?? "").trim();
  }).map(({ field, id, label }) => ({
    key: `${prefix}.${field}`,
    inputId: `${prefix}-${id}`,
    label,
    group: prefix,
  }));
}

/**
 * Whether a field carries a star, from the same list that decides whether it
 * blocks. A star that does not match what the form actually enforces is worse
 * than no star: it teaches people that the stars are decoration.
 */
export function isAddressFieldRequired(field: keyof Address, purpose: Purpose = "booking"): boolean {
  if (!ADDRESS_REQUIRED.some((entry) => entry.field === field)) return false;
  return purpose === "booking" || ROUTING_FIELDS.has(field);
}

/** The red marks, keyed the way AddressForm expects. */
export function toFieldErrors(missing: Missing[]): Record<string, string> {
  return Object.fromEntries(missing.map((m) => [m.key, "This is needed to continue"]));
}

/**
 * One sentence naming what is outstanding.
 *
 * Capped, because a sentence listing fourteen fields is read by nobody. Beyond
 * the cap it counts the rest, which is still more useful than silence.
 */
export function summariseMissing(missing: Missing[], cap = 3): string {
  if (missing.length === 0) return "";

  // Name the leg once rather than on every field. Two missing delivery fields
  // used to read "contact name for delivery and phone number for delivery",
  // which is the same four words twice and reads like a machine wrote it.
  // Only when EVERY field belongs to the same leg. A parcel field carries no
  // leg, and filing one under "for delivery" would be worse than the repetition
  // this is here to remove.
  const groups = new Set(missing.map((m) => m.group));
  const shared = groups.size === 1 && !groups.has(undefined) ? [...groups][0] : undefined;

  const shown = missing.slice(0, cap).map((m) => (shared || !m.group ? m.label : `${m.label} for ${m.group}`));
  const rest = missing.length - shown.length;
  const list =
    shown.length === 1
      ? shown[0]
      : `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`;

  const lead = shared ? `Still needed for ${shared}` : "Still needed";
  return rest > 0 ? `${lead}: ${list}, and ${rest} more.` : `${lead}: ${list}.`;
}

/**
 * Put the cursor in the first thing that is missing, and bring it into view.
 *
 * Focus rather than only scrolling: a screen reader announces the field and its
 * error, and on a phone the keyboard opens on the field that needs typing
 * instead of leaving the person to find it.
 */
export function focusFirstMissing(missing: Missing[]): void {
  const first = missing[0];
  if (!first) return;
  const el = document.getElementById(first.inputId);
  if (!el) return;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  // After the scroll is queued, so the browser does not fight itself.
  window.setTimeout(() => (el as HTMLElement).focus({ preventScroll: true }), 150);
}
